// src/components/OverViewsPage/SurgicalHistory/SurgicalHistory.jsx
"use client";

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./SurgicalHistory.css";
import TitleIcon from "@/assets/icons/Icon-surgery.png";
import ArrowIcon from "@/assets/icons/arroww.png";

const SurgicalHistory = ({
  editable = false,
  onChange,
  items = [],
  variant = "default", // 'default' | 'overview'
}) => {
  const { t } = useTranslation();
  const [newLabel, setNewLabel] = useState("");
  const [newYear, setNewYear] = useState("");
  const [addError, setAddError] = useState("");

  const isOverview = variant === "overview";
  const displayItems = items;
  const hasItems = displayItems.length > 0;

  const handleEdit = (id, field, value) => {
    const next = items.map((it) =>
      it.id === id
        ? {
            ...it,
            [field]: field === "year" ? value.replace(/[^\d]/g, "") : value,
          }
        : it
    );
    onChange?.(next);
  };

  const handleDelete = (id) => {
    onChange?.(items.filter((it) => it.id !== id));
  };

  const handleAdd = () => {
    const label = newLabel.trim();
    if (!label) return;

    /* L'année est optionnelle (la date l'est aussi côté API). En revanche une
       année hors bornes (ex: "12") faisait un `return` MUET : le clic sur "+"
       ne produisait rien du tout, aucune ligne, aucun message. On le dit
       maintenant explicitement. */
    let year;
    if (String(newYear).trim() !== "") {
      const yearNum = Number(newYear);
      if (!yearNum || yearNum < 1900 || yearNum > 2100) {
        setAddError(t("surgicalHistory.invalidYear"));
        return;
      }
      year = yearNum;
    }

    // pas de `backendId` → la sauvegarde la créera (POST)
    const next = [...items, { id: `temp-${Date.now()}`, backendId: null, label, year }];
    onChange?.(next);
    setNewLabel("");
    setNewYear("");
    setAddError("");
  };

  /* Le brouillon de la ligne "ajouter" n'existait que dans cet état local :
     taper un nom puis cliquer directement sur "Save changes" le perdait
     silencieusement (rien n'était enregistré, aucune erreur). On le valide
     dès que le focus quitte le bloc d'ajout — sauf s'il reste à l'intérieur
     (tabulation vers l'année, clic sur "+"). */
  const commitDraftOnFocusOut = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    handleAdd();
  };

  return (
    <section className={`sh-card ${isOverview ? "sh-card--overview" : ""}`}>
      <div className="sh-head">
        <div className="sh-title-icon">
          <img src={TitleIcon} alt="" width={24} height={24} />
        </div>
        <h4 className="sh-title">{t("surgicalHistory.title")}</h4>
      </div>

      <div className="sh-sep" />

      <div className="sh-subhead">
        <img src={ArrowIcon} alt="" width={10} height={10} />
        <span className="sh-subtitle">{t("surgicalHistory.latestSurgeries")}</span>
      </div>

      {!editable ? (
        hasItems ? (
          <ul className={`sh-list ${isOverview ? "sh-list--compact" : ""}`}>
            {displayItems.map((it) => (
              <li
                key={it.id}
                aria-label={t("surgicalHistory.itemAria", {
                  label: it.label,
                  year: it.year || "—",
                })}
              >
                <span className="dot" aria-hidden="true" />
                <div className="sh-item-main">
                  <span className="text">
                    {it.label || t("surgicalHistory.unknownSurgery")}
                  </span>
                  <span className="year">
                    {it.year ? it.year : t("surgicalHistory.yearUnknown")}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="sh-empty">{t("surgicalHistory.noSurgeries")}</div>
        )
      ) : (
        <div className="sh-edit">
          <ul className="sh-edit-list">
            {items.map((it) => (
              <li key={it.id} className="sh-edit-item">
                <input
                  className="sh-input sh-input--label"
                  type="text"
                  value={it.label}
                  onChange={(e) => handleEdit(it.id, "label", e.target.value)}
                  placeholder={t("surgicalHistory.surgeryLabel")}
                />
                <input
                  className="sh-input sh-input--year"
                  type="text"
                  inputMode="numeric"
                  value={String(it.year ?? "")}
                  onChange={(e) => handleEdit(it.id, "year", e.target.value)}
                  placeholder={t("surgicalHistory.yearPlaceholder")}
                  maxLength={4}
                />
                <button
                  className="sh-del-btn"
                  onClick={() => handleDelete(it.id)}
                  aria-label={t("surgicalHistory.deleteButton")}
                  type="button"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>

          <div className="sh-add" onBlur={commitDraftOnFocusOut}>
            <input
              className="sh-input sh-input--label"
              type="text"
              placeholder={t("surgicalHistory.addSurgeryPlaceholder")}
              value={newLabel}
              onChange={(e) => { setNewLabel(e.target.value); setAddError(""); }}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
            <input
              className="sh-input sh-input--year"
              type="text"
              inputMode="numeric"
              placeholder={t("surgicalHistory.yearPlaceholder")}
              value={newYear}
              onChange={(e) => { setNewYear(e.target.value.replace(/[^\d]/g, "")); setAddError(""); }}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              maxLength={4}
            />
            <button
              className="sh-add-btn"
              onClick={handleAdd}
              aria-label={t("surgicalHistory.addButton")}
              type="button"
            >
              +
            </button>
          </div>

          {addError ? (
            <div className="sh-add-error" role="alert">
              {addError}
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
};

export default SurgicalHistory;